import { Text } from '@noorden-insurtech/alba-design-system';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type {
  CgPolicyCoverageGroup,
  CgPolicySimpleGroup,
  CgPolicyStipulant,
} from './map-policy-details-to-capital-global-view.util';

function RiskDataCard({ items }: { items: CgPolicyStipulant['riskItems'] }) {
  if (!items.length) return null;
  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-sm text-alba-black-500'>Dados do Risco</Text>
      <div className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'>
        {items.map((item, index) => (
          <div key={item.label} className='flex flex-col gap-2'>
            {index > 0 ? <div className='h-px w-full bg-alba-gray-200' /> : null}
            <div className='flex items-center justify-between gap-4'>
              <Text className='text-sm font-bold text-alba-black-500'>{item.label}</Text>
              <Text className='text-sm text-alba-gray-600'>{item.value}</Text>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CoverageGroups({ groups }: { groups: CgPolicyCoverageGroup[] }) {
  if (!groups.length) return null;
  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-sm text-alba-black-500'>Coberturas</Text>
      <div className='flex flex-col gap-4'>
        {groups.map(group => (
          <div
            key={group.title}
            className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'
          >
            <div className='flex items-center justify-between gap-4'>
              <Text className='flex-1 text-sm font-bold text-alba-black-500'>
                {group.title}
              </Text>
              <div className='flex shrink-0 items-center gap-8'>
                <Text className='text-xs font-medium text-alba-gray-600'>Capital global</Text>
                <Text className='text-xs font-medium text-alba-gray-600'>Prêmio</Text>
              </div>
            </div>
            <div className='h-px w-full bg-alba-gray-200' />
            {group.items.map(item => (
              <div
                key={`${group.title}-${item.name}`}
                className='flex items-start justify-between gap-4'
              >
                <Text className='flex-1 text-xs text-alba-gray-600'>{item.name}</Text>
                <div className='flex shrink-0 items-center gap-8'>
                  <Text className='text-right text-xs text-alba-gray-600'>{item.capital}</Text>
                  <Text className='text-right text-xs text-alba-gray-600'>{item.premium}</Text>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function AssistanceCapitalizationGroups({ groups }: { groups: CgPolicySimpleGroup[] }) {
  if (!groups.length) return null;
  return (
    <div className='flex flex-col gap-4'>
      <Text className='text-sm text-alba-black-500'>Assistências e Capitalização</Text>
      <div className='flex flex-col gap-4'>
        {groups.map(group => (
          <div
            key={group.title}
            className='flex flex-col gap-2 rounded-xl border border-alba-gray-400 p-4'
          >
            <Text className='text-sm font-bold text-alba-black-500'>{group.title}</Text>
            <div className='h-px w-full bg-alba-gray-200' />
            {group.items.map(item => (
              <Text key={`${group.title}-${item}`} className='text-xs text-alba-gray-600'>
                {item}
              </Text>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

type CardProps = {
  entry: CgPolicyStipulant;
  isOpen: boolean;
  onToggle: () => void;
};

export function CapitalGlobalStipulantCard({ entry, isOpen, onToggle }: CardProps) {
  const hasContent =
    entry.riskItems.length > 0 ||
    entry.coverageGroups.length > 0 ||
    entry.assistanceCapitalizationGroups.length > 0;

  return (
    <div className='flex flex-col overflow-hidden rounded-xl border border-alba-gray-400'>
      <button
        type='button'
        className='flex w-full items-center justify-between px-4 py-4 text-left'
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <Text className='text-sm font-bold text-alba-black-500'>{entry.label}</Text>
        {isOpen ? (
          <ChevronUp className='h-5 w-5 shrink-0 text-alba-gray-600' strokeWidth={1.6} />
        ) : (
          <ChevronDown className='h-5 w-5 shrink-0 text-alba-gray-600' strokeWidth={1.6} />
        )}
      </button>

      {isOpen && hasContent ? (
        <div className='flex flex-col gap-6 border-t border-alba-gray-400 px-4 py-4'>
          <RiskDataCard items={entry.riskItems} />
          <CoverageGroups groups={entry.coverageGroups} />
          <AssistanceCapitalizationGroups
            groups={entry.assistanceCapitalizationGroups}
          />
        </div>
      ) : null}
    </div>
  );
}
